// src/components/ui/ActionMenu/ActionMenu.jsx
// Row-actions renderer: shows every action as an icon button when there are at most
// `maxVisible`, otherwise the first `maxVisible - 1` plus a "more" button opening a popover.
// The popover is position: fixed so frozen/overflow-hidden table columns never clip it.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './ActionMenu.module.css';

const MENU_WIDTH = 220;
const VIEWPORT_GUTTER = 8;

const ActionMenu = ({ actions = [], maxVisible = 4, triggerClassName = '', className = '' }) => {
  const { t } = useLocalization();
  const [menuPosition, setMenuPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const visibleActions = actions.filter(action => action && !action.hidden);
  const needsOverflow = visibleActions.length > maxVisible;
  const inlineActions = needsOverflow ? visibleActions.slice(0, maxVisible - 1) : visibleActions;
  const overflowActions = needsOverflow ? visibleActions.slice(maxVisible - 1) : [];

  const closeMenu = useCallback(() => setMenuPosition(null), []);

  const openMenu = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    const estimatedHeight = overflowActions.length * 40 + 12;
    const opensUp = rect.bottom + estimatedHeight + VIEWPORT_GUTTER > window.innerHeight;
    setMenuPosition({
      top: opensUp ? Math.max(VIEWPORT_GUTTER, rect.top - estimatedHeight - 4) : rect.bottom + 4,
      left: Math.max(VIEWPORT_GUTTER, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - VIEWPORT_GUTTER)),
    });
  };

  useEffect(() => {
    if (!menuPosition) return undefined;
    const handleMouseDown = (e) => {
      if (menuRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      closeMenu();
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [menuPosition, closeMenu]);

  const renderInline = ({ key, icon: Icon, label, onClick, to, className: actionClassName, disabled }) => (
    to ? (
      <Link key={key} to={to} className={actionClassName} title={label} aria-label={label}>
        <Icon size={16} />
      </Link>
    ) : (
      <button key={key} type="button" onClick={onClick} className={actionClassName} title={label} aria-label={label} disabled={disabled}>
        <Icon size={16} />
      </button>
    )
  );

  const renderMenuItem = ({ key, icon: Icon, label, onClick, to, disabled, danger }) => {
    const itemClassName = `${styles.menuItem} ${danger ? styles.dangerItem : ''}`;
    return to ? (
      <Link key={key} to={to} className={itemClassName} role="menuitem" onClick={closeMenu}>
        <Icon size={16} />
        <span>{label}</span>
      </Link>
    ) : (
      <button
        key={key}
        type="button"
        className={itemClassName}
        role="menuitem"
        disabled={disabled}
        onClick={() => { closeMenu(); onClick?.(); }}
      >
        <Icon size={16} />
        <span>{label}</span>
      </button>
    );
  };

  return (
    <div className={`${styles.actionMenu} ${className}`}>
      {inlineActions.map(renderInline)}
      {needsOverflow && (
        <button
          ref={triggerRef}
          type="button"
          className={`${triggerClassName} ${menuPosition ? styles.triggerActive : ''}`}
          onClick={() => (menuPosition ? closeMenu() : openMenu())}
          title={t('common.moreActions')}
          aria-label={t('common.moreActions')}
          aria-haspopup="menu"
          aria-expanded={!!menuPosition}
        >
          <MoreVertical size={16} />
        </button>
      )}
      {menuPosition && createPortal(
        <div
          ref={menuRef}
          className={styles.menu}
          role="menu"
          style={{ top: menuPosition.top, left: menuPosition.left, width: MENU_WIDTH }}
        >
          {overflowActions.map(renderMenuItem)}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionMenu;
